import React from 'react';
import { SxProps } from '@mui/system';
import { styled, Theme } from '@mui/material/styles';
import { AlertColor } from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import MenuIcon from '@mui/icons-material/Menu';
import { HeaderUserActions } from './internal/HeaderUserActions';
import { IngramLogoWithoutR } from '../icons/IngramLogoWithoutR';
import { useIsDown } from '../responsive';
import Button from '@mui/material/Button';
import { useLocation, useNavigate } from 'react-router';
import { useLocalization, useTranslate } from '@im/react-localization';
import Typography from '@mui/material/Typography';
import { AvatarProps } from './internal/Avatar';
import { Link, useSearchParams } from 'react-router-dom';
import { Option } from '@im/ui-lib/SelectOption';
import { IngramLogoLogOutWithoutR } from '../icons/IngramLogoLogOutWithoutR';
import { SelectChangeEvent } from '@mui/material/Select';

type Props = {
  AvatarProps: AvatarProps;
  availableLanguages: string[];
  countryOptions: Option[];
  'data-testid'?: string;
  isAuthenticated: boolean;
  isLogoClickable?: boolean;
  onCountryChange?: (
    event: SelectChangeEvent<unknown>,
    child: React.ReactNode
  ) => void;
  onLanguageChange?: (
    event: SelectChangeEvent<unknown>,
    child: React.ReactNode
  ) => void;
  onMenuButtonClick: () => void;
  selectedCountry: string;
  selectedLanguage?: string;
  showAvatar?: boolean;
  showImpersonationHeader?: boolean;
  sx?: SxProps<Theme>;
};

export type ImpersonationHeaderProps = {
  alertMessage: string | JSX.Element;
  alertSeverity: AlertColor;
  exitButtonDisabled: boolean;
  exitButtonLoading: boolean;
  exitButtonText: string | JSX.Element;
  onImpersonationButtonClick: () => void;
};
export const HEADER_HEIGHT = 52;

export function Header(props: Readonly<Props>) {
  const {
    AvatarProps,
    countryOptions,
    'data-testid': testid = 'required',
    isAuthenticated,
    isLogoClickable = true,
    onCountryChange,
    onMenuButtonClick,
    selectedCountry,
    showAvatar = true,
    showImpersonationHeader = false,
    sx,
  } = props;

  const isDownLg = useIsDown('lg', true);

  const showMenuIcon = isDownLg;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { translate } = useLocalization();
  const ariaLabelTranslate = useTranslate();
  const location = useLocation();
  const route = searchParams.get('countryCode')
    ? `/?countryCode=${searchParams.get(
        'countryCode'
      )}&companyCode=${searchParams.get('companyCode')}`
    : '/';
  return (
    <RootStack alignContent="center" direction="row" sx={sx}>
      <Stack direction="row" spacing={2}>
        {showMenuIcon && !showImpersonationHeader && (
          <StyledIcon
            aria-label={ariaLabelTranslate('Common.Menu')}
            color="primary"
            onClick={onMenuButtonClick}
          >
            <MenuIcon height="24px !important" width="24px !important" />
          </StyledIcon>
        )}

        <Stack alignItems="center" direction="row">
          {isLogoClickable ? (
            <StyledLink
              aria-label={ariaLabelTranslate('Common.GoToHomePage')}
              to={route}
            >
              {isAuthenticated ? (
                <IngramLogoWithoutR height="24px" width="242px" />
              ) : (
                <IngramLogoLogOutWithoutR height="24px" width="242px" />
              )}
            </StyledLink>
          ) : (
            <>
              {isAuthenticated ? (
                <IngramLogoWithoutR height="24px" width="242px" />
              ) : (
                <IngramLogoLogOutWithoutR height="24px" width="242px" />
              )}
            </>
          )}
        </Stack>
      </Stack>

      {isAuthenticated && (
        <HeaderUserActions
          AvatarProps={AvatarProps}
          availableLanguages={props.availableLanguages}
          countryOptions={countryOptions}
          data-testid={testid}
          onCountryChange={onCountryChange}
          onLanguageChange={props.onLanguageChange}
          selectedCountry={selectedCountry}
          selectedLanguage={props.selectedLanguage}
          showAvatar={showAvatar}
        />
      )}
      {!isAuthenticated && (
        <Stack alignContent="right" direction="row" spacing="1.286rem">
          <Button
            data-testid="btn_TopCornerLogin"
            onClick={() => navigate('/login', { state: { from: location } })}
            sx={{ height: '32px', padding: '6px 12px' }}
            variant="contained"
          >
            <Typography variant="bodycopyregular">
              {translate('OktaWidget.PrimaryAuthSubmit')}
            </Typography>
          </Button>
        </Stack>
      )}
    </RootStack>
  );
}

const RootStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  height: '52px',
  justifyContent: 'space-between',
  padding: '0px 16px',
}));
const StyledLink = styled(Link)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
}));

const StyledIcon = styled(IconButton)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
  },
}));
